import React from 'react';
import { PropTypes } from 'prop-types';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import config from 'config';


import { utilsService } from '../../core/services/utils.service';
import { DownloadLink } from '../link/download-link';
import { DatePicker } from '../date-picker/date-picker.jsx';
import RadioGroup from '../forms/radio-group';
import TextInput from '../forms/text-input';
import { AppDetailInfoContentRow } from '../app-detail/app-detail-info-content-row.jsx';
import AppFormCheckboxList from '../../pages/parts/_app-form-checkbox-list.jsx';
import AppFormGroupCheckboxList from '../../pages/parts/_app-form-group-checkbox-list.jsx';
import IconDropzone from '../dropzone/icon-dropzone';
import ScreenshotDropzone from '../dropzone/screenshot-dropzone';
import ScreenshotPreview from '../screenshot-preview/screenshot-preview';
import { AsideLink } from '../aside-link/aside-link';

@observer
export default class AppEditableInfo extends React.Component {

  static propTypes = {
    masterStore: PropTypes.object.isRequired,
    readOnly: PropTypes.bool
  }

  static defaultProps = {
    readOnly: true
  }

  constructor(props) {
    super(props);
    this.masterStore = this.props.masterStore;
    this.newAppMasterStore = this.masterStore.newAppMasterStore;
    this.newAppDetailsStore = this.newAppMasterStore.newAppDetailsStore;
    this.newAppValidationsStore = this.newAppMasterStore.newAppValidationsStore;
    this.references = {};
    this.downloadChecklist = this.masterStore.downloadDeveloperChecklistFile.bind(this.masterStore);

    reaction(() => this.newAppValidationsStore.errorDestination, () => {
      this.placeFocus()
    });
  }

  placeFocus = () => {
    this.newAppValidationsStore.placeFocus({
      references: this.references,
      defaultFieldId: 'shortDescription'
    });
  }

  saveRef = (name, ref) => {
    this.references[name] = ref;
  }

  onCustomDateChange = (date) => {
    this.newAppDetailsStore.handleCustomDateChange(date);
  }

  renderStaticOrEditableReleaseDate = () => {
    if (this.props.readOnly || this.newAppMasterStore.newAppData.status === "LIVE") {
      return this.renderStaticDate('releaseDate', 'Release date');
    } else {
      return this.renderEditReleaseDate();
    }
  }

  renderEditReleaseDate = () => {
    const optionsList = [
      {
        title: 'Immediately upon approval',
        hiddenValue: 'IMMEDIATELY'
      },
      {
        title: 'Custom Date',
        hiddenValue: 'CUSTOM',
        additionalJsx: (
          <span className="help-text col-12">Your app will be released after 3am EST on the date selected.</span>
        )
      }
    ];
    return [
      <RadioGroup
        id="release-date"
        key="release-date"
        ref={(i) => this.references['releaseDate'] = i}
        value={this.newAppDetailsStore.releaseDateType}
        updateValue={newValue => { this.newAppDetailsStore.handleReleaseDateChange(newValue) }}
        required={true}
        labelText="Scheduled release date"
        optionsList={optionsList}
        className={this.newAppDetailsStore.releaseDateIsValid ? '' : 'has-error'}
        errorMessage={this.newAppMasterStore.REQUIRED_ERROR}
        showErrorDirectiveFromMaster={this.newAppMasterStore.masterStateProps.errorsAreDisplayed} />,
      <DatePicker
        id="custom-release-date"
        key="custom-release-date"
        handleOnChange={this.onCustomDateChange}
        disabled={this.newAppDetailsStore.releaseDateType !== "CUSTOM"}
        uniqueSrText="Please choose a custom release date."
        utcOffset="-5:00"
        setHourTo={3}
        date={this.newAppMasterStore.newAppData.releaseDate} />
    ];
  }

  renderStaticDate(newAppDataKey, title) {
    let date = this.newAppMasterStore.newAppData[newAppDataKey];
    if(date) {
      let dateString = utilsService.normalizeReleaseDate(date);
      return <AppDetailInfoContentRow title={title} content={dateString} wasEdited={this.wasEdited(newAppDataKey)} />
    }
    return null;
  }

  renderCertificateDownloadField = () => {
    const app = this.newAppMasterStore.newAppData
    const timestamp = app.caFormUploadDate;
    return (<DownloadLink title={`${app.caFormFileName}.${app.caFormFileExtension}`} onClick={this.downloadChecklist} srText="Download current file" smallText={timestamp
      ? `Uploaded ${utilsService.normalizedDate(timestamp, 'MMMM Do YYYY | h:mm:ss a')}`
      : ''} />)
  }

  renderAppFileField = () => {
    const app = this.newAppMasterStore.newAppData
    const timestamp = app.fileUploadDateTime;
    return (<div>
      {app.appFileName} | Uploaded {utilsService.normalizedDate(timestamp, 'MMMM Do YYYY | h:mm:ss a')}
    </div>)
  }

  onDetailsChange = (name, newValue) => {
    this.newAppDetailsStore.handleInput(name, newValue);
  }

  wasEdited = (field) => {
    const diff = this.newAppMasterStore.diffNewAppData();
    return diff.includes(field);
  }

  renderInput = ({ name, className = '', type = 'text', onBlur }) => {
    const fieldInfo = this.newAppDetailsStore.inputFields[name];
    const helpText = fieldInfo.subtext && fieldInfo.subtext.join(' ');
    const errorText = fieldInfo.errorList.length > 0 ? fieldInfo.errorList.join(' ') : '';

    return <TextInput
      id={name}
      type={type}
      ref={(i) => { this.references[name] = i }}
      required={fieldInfo.required}
      charLimit={fieldInfo.validations.maxChars}
      labelText={fieldInfo.label}
      helperText={helpText}
      value={this.newAppMasterStore.newAppData[name]}
      updateValue={(newValue) => { this.onDetailsChange(name, newValue) }}
      onBlur={onBlur}
      format={fieldInfo.validations.format}
      errorMessage={errorText}
      className={className}
      showErrorDirectiveFromMaster={this.newAppMasterStore.masterStateProps.errorsAreDisplayed} />;
  }

  renderDescriptionVersionInformation = () => {
    let appType = this.newAppMasterStore.newAppData.app_type;
    let checklistTitle = 'Uploaded files';
    if (/CERTIFIED/i.test(appType)) {
      appType = 'FirstNet Certified';
      checklistTitle = 'Certified app files';
    } else if (/GENERAL/i.test(appType)) {
      appType = 'FirstNet Verified';
      checklistTitle = 'Verified app checklist';
    }
    return (
      <section id="desc_version">
        <h3 className="as-h4">Description &amp; Version Information</h3>
        <div className="row">
          <div className={this.props.readOnly ? 'col-12 hide-aside-links' : 'col-8'}>
          {this.renderStaticOrEditableReleaseDate()}
          </div>
          <div className="col-4 aside-link faq">
            Need additional help? Check out our:<br />
            <AsideLink
              isInternal={true}
              to="/faq"
              text="Relevant FAQs" />
          </div>
        </div>
        {this.renderStaticDate('removalDate', 'Removal date')}
        {this.renderStaticDate('withdrawnDate', 'Withdrawn date')}
        <AppDetailInfoContentRow title="Listing type" content={this.newAppMasterStore.newAppData.listingType === 'CROSSLISTED' ? 'Cross-listed' : 'Hosted'} />
        <AppDetailInfoContentRow title="App approval type" className="capitalize-content" content={appType} />
        <AppDetailInfoContentRow title={checklistTitle} content={this.renderCertificateDownloadField()} />
        <div className="row">
          <div className={this.props.readOnly ? 'col-12 hide-aside-links' : 'col-8'}>
            {this.props.readOnly
              ? <AppDetailInfoContentRow wasEdited={this.wasEdited('shortDescription')} title="Short description" content={this.newAppMasterStore.newAppData.shortDescription} />
              : this.renderInput({ name: 'shortDescription', type: 'textarea' })}
            </div>
            <div className={`col-4 aside-link writing-descriptions`}>
                Need help writing these descriptions?<br />
                <AsideLink
                  to={config.devMarketingYourApp}
                  text="Developer Portal: Marketing your App" />
              </div>
            <div className={this.props.readOnly ? 'col-12 hide-aside-links' : 'col-8'}>
            {this.props.readOnly
              ? <AppDetailInfoContentRow wasEdited={this.wasEdited('longDescription')} title="Long description" content={this.newAppMasterStore.newAppData.longDescription} />
              : this.renderInput({ name: 'longDescription', type: 'textarea' })}
            </div>
          </div>
        <AppDetailInfoContentRow title="Version Notes" content={this.newAppMasterStore.newAppData.versionNotes} />
        <AppDetailInfoContentRow title="Device" content={this.newAppDetailsStore.deviceAsHumanReadable} />
      </section>
    )
  }

  renderSearchFilterCriteria = () => {
    return (
      <section id="search_filter_criteria">
        <h3 className="as-h4">Search &amp; Filter Criteria</h3>
        {this.props.readOnly
          ? <AppDetailInfoContentRow wasEdited={this.wasEdited('category')} title="Category" content={this.newAppMasterStore.getDisplayList('category')} />
          : <AppFormCheckboxList newAppMasterStore={this.newAppMasterStore} type={'category'} required={true} saveRef={ref => this.saveRef('category', ref)} />}
        {this.props.readOnly
          ? <AppDetailInfoContentRow wasEdited={this.wasEdited('segments')} title="Branch/Discipline" content={this.newAppMasterStore.getDisplayList('segments')} />
          : <AppFormCheckboxList newAppMasterStore={this.newAppMasterStore} type={'segments'} required={true} saveRef={ref => this.saveRef('segments', ref)} />}
        {this.props.readOnly
          ? <AppDetailInfoContentRow wasEdited={this.wasEdited('searchTags')} title="Search Tags" content={this.newAppMasterStore.getDisplayList('searchTags')} />
          : <AppFormGroupCheckboxList newAppMasterStore={this.newAppMasterStore} type={'searchTags'} group={'searchTagGroups'} required={true} saveRef={ref => this.saveRef('searchTags', ref)} />}
        {this.props.readOnly
          ? <AppDetailInfoContentRow wasEdited={this.wasEdited('tags')} title="Search keywords" content={this.newAppMasterStore.newAppData.tags} />
          : this.renderInput({ name: 'tags', type: 'textarea' })}
        <AppDetailInfoContentRow title="App capabilities" content={this.newAppMasterStore.getDisplayList('capabilities')} />
        {this.props.readOnly
          ? <AppDetailInfoContentRow wasEdited={this.wasEdited('certifications')} title="Certifications" content={this.newAppMasterStore.getDisplayList('certifications')} />
          : <AppFormCheckboxList newAppMasterStore={this.newAppMasterStore} type={'certifications'} required={false} saveRef={ref => this.saveRef('certifications', ref)} />}
      </section>
    )
  }

  renderDeveloperInfo = () => {
    return (
      <section id="developer_info">
        <h3 className="as-h4">Developer Information</h3>
        <AppDetailInfoContentRow title="Developer company" content={this.newAppMasterStore.newAppData.authorName} />
        <AppDetailInfoContentRow title="About the developer" content={this.newAppMasterStore.newAppData.aboutDev} />
        <AppDetailInfoContentRow title="Developer website" content={this.newAppMasterStore.newAppData.devWebsite} />
      </section>
    )
  }

  renderStorePackageInfo = () => {
    const isCrossListed = this.newAppMasterStore.newAppData.listingType === 'CROSSLISTED';
    let sectionTitle = 'Package Information';
    if (isCrossListed) {
      sectionTitle = `Store & ${sectionTitle}`;
      var storeName = this.newAppMasterStore.isAndroidApp ? 'Google Play' : 'App Store';
    }

    return (
      <section id="store_pkg_info">
        <h3 className="as-h4">{sectionTitle}</h3>
        {isCrossListed &&
          <React.Fragment>
            <AppDetailInfoContentRow title={`${storeName} URL`} content={this.newAppMasterStore.newAppData.storeLink} />
            <AppDetailInfoContentRow title={`${storeName} URL type`} content={this.newAppMasterStore.newAppData.urlType && this.newAppMasterStore.newAppData.urlType === 'E' ? 'Enterprise store' : 'Consumer store'} />
            <AppDetailInfoContentRow title={`${storeName} version`} content={this.newAppMasterStore.isAndroidApp ? this.newAppMasterStore.newAppData.storeVersion : this.newAppMasterStore.newAppData.version} />
          </React.Fragment>
        }
        {this.newAppMasterStore.newAppData.appFileName &&
          <AppDetailInfoContentRow title="App package file" content={this.renderAppFileField()} />
        }
      </section>
    )
  }

  renderScreenshotPreviews = () => {
    return (
      <React.Fragment>
        <ScreenshotPreview title="phone" screenshots={this.newAppMasterStore.newAppData.mobileURLAndDescription} />
        <ScreenshotPreview title="tablet" screenshots={this.newAppMasterStore.newAppData.tabletURLAndDescription} />
      </React.Fragment>
    )
  }

  renderImages = () => {
    return (
      <section id="app_images">
        <h3 className="as-h4">Images</h3>
        <IconDropzone newAppMasterStore={this.newAppMasterStore} uploadImmediately={false} disabled={false} saveRef={this.saveRef} readOnly={this.props.readOnly} wasEdited={this.wasEdited('iconImageURLAndDescription')} />
        {this.props.readOnly
          ? this.renderScreenshotPreviews()
          : <ScreenshotDropzone newAppMasterStore={this.newAppMasterStore} uploadImmediately={false} disabled={false} saveRef={this.saveRef} />}
      </section>
    )
  }

  render() {
    return (
        <div className="review edit-app">
          <div>
            {this.renderDescriptionVersionInformation()}
            {this.renderSearchFilterCriteria()}
            {this.renderDeveloperInfo()}
            {this.renderStorePackageInfo()}
          </div>
          <div>
            {this.renderImages()}
          </div>
        </div>
    )
  }
}
